import { ChangeEvent, FormEvent, ReactNode, useEffect, useState } from "react";
import {
  ValidationMessage,
  ButtonVariant,
  InputWithButton,
  Button,
  InputWithButtonI,
  InputStatus,
  Loading,
} from "components";
import { twMerge } from "tailwind-merge";
import { UserCognito, storePhoneNumber, storePreferredMFA } from "helpers";
import { updateUserAttributes, sendUserAttributeVerificationCode, confirmUserAttribute } from "aws-amplify/auth";
import { useUpdateProfileMutation } from "services";
import { t } from "i18next";

type SubmitType = "phoneNumber" | "code";

const initialData: InputWithButtonI = {
  value: "",
  status: "initial",
  error: "",
};

interface VerifyPhoneNumberFormProps {
  user: UserCognito;
  variant: "auth" | "profile";
  onContinue: () => void;
  cancel: ReactNode;
  onStatusChange?: ({ phoneNumber, code }: { phoneNumber: InputStatus; code: InputStatus }) => void;
}

const getError = (err: any): string => {
  const errorMessages: Record<string, string> = {
    InvalidParameterException: t("validation.invalidPhoneNumberFormat"),
    CodeMismatchException: t("validation.invalidVerificationCode"),
    LimitExceededException: t("profile.twoFactorAuth.tooManyAttempts"),
    NotAuthorizedException: t("profile.twoFactorAuth.notAuthorized"),
  };
  return errorMessages[err.name] || err.message || t("profile.twoFactorAuth.anErrorOccured");
};

export const VerifyPhoneNumberForm = ({
  user,
  onContinue,
  cancel,
  onStatusChange = () => {},
}: VerifyPhoneNumberFormProps) => {
  console.log(user);
  const [phoneNumber, setPhoneNumber] = useState<InputWithButtonI>({
    ...initialData,
    value: user?.attributes?.phone_number || "",
  });
  const [code, setCode] = useState<InputWithButtonI>(initialData);
  const [sent, setSent] = useState<boolean>(false);
  const [disabledPhoneNumber, setDisabledPhoneNumber] = useState<boolean>(true);
  const [disabledCode, setDisabledCode] = useState<boolean>(true);
  const [disabled, setDisabled] = useState<boolean>(true);
  const [updateUser] = useUpdateProfileMutation();
  const [isLoadingGetAttribute, setIsLoadingGetAttribute] = useState<boolean>(false);
  const [isLoadingVerifyAttribute, setIsLoadingVerifyAttribute] = useState<boolean>(false);

  const returnPhoneNumberText =
    phoneNumber.status === "initial"
      ? t("profile.twoFactorAuth.send")
      : phoneNumber.status === "success"
        ? t("profile.twoFactorAuth.sent")
        : t("profile.twoFactorAuth.sendAgain");

  const returnCodeText = code.status === "initial" ? "Confirm" : code.status === "success" ? "Confirmed" : "Try again";

  const returnButtonVariant = ({ status }: InputWithButtonI): ButtonVariant =>
    status === "initial" ? "bg-primary" : status === "success" ? "bg-success" : "bg-error";

  const resetState = () => {
    setPhoneNumber({ ...initialData, value: user.attributes.phone_number || "" });
    setCode(initialData);
    setDisabledPhoneNumber(true);
    setDisabledCode(true);
    setSent(false);
  };

  const onChangeHandler = (event: ChangeEvent<HTMLInputElement>): void => {
    const {
      target: { name, value },
    } = event;

    if (name === "phoneNumber") {
      setPhoneNumber((oldObject) => ({
        ...oldObject,
        value,
      }));
    }

    if (name === "code") {
      setCode((oldObject) => ({
        ...oldObject,
        value,
      }));
    }
  };

  const onVerifyPhoneNumber = async (phoneNumberValue: string) => {
    setDisabledPhoneNumber(true);
    setIsLoadingGetAttribute(true);
    try {
      if (phoneNumberValue !== user.attributes.phone_number) {
        await updateUserAttributes({ userAttributes: { phone_number: phoneNumberValue } });
        await updateUser({ phone_number: phoneNumberValue });
      }
      await sendUserAttributeVerificationCode({ userAttributeKey: "phone_number" });
      setPhoneNumber((oldObject) => ({
        ...oldObject,
        status: "success",
      }));
      setSent(true);
    } catch (err: any) {
      setPhoneNumber((oldObject) => ({
        ...oldObject,
        error: getError(err),
        status: "error",
      }));
    } finally {
      setDisabledPhoneNumber(false);
      setIsLoadingGetAttribute(false);
    }
  };

  const onVerifyCode = async (code: string) => {
    setDisabledCode(true);
    setIsLoadingVerifyAttribute(true);
    try {
      await confirmUserAttribute({ userAttributeKey: "phone_number", confirmationCode: code });
      storePhoneNumber(phoneNumber.value);
      setCode((oldObject) => ({
        ...oldObject,
        status: "success",
      }));
      onContinue();
    } catch (err: any) {
      setCode((oldObject) => ({
        ...oldObject,
        error: getError(err),
        status: "error",
      }));
    } finally {
      setDisabledCode(false);
      setIsLoadingVerifyAttribute(false);
    }
  };

  const onSubmitHandler = (event: FormEvent<HTMLFormElement | HTMLButtonElement>, submitType: SubmitType): void => {
    event.preventDefault();
    const phone_number = phoneNumber.value;
    console.log("Submitting phone number:", phone_number);
    if (submitType === "phoneNumber" && phoneNumber.status !== "success") {
      onVerifyPhoneNumber(phone_number);
    }
    if (submitType === "code" && code.status !== "success") {
      onVerifyCode(code.value);
    }
  };

  useEffect(() => {
    const phoneRegex = /^\+389[0-9]{8}$/;
    setDisabledPhoneNumber(!phoneRegex.test(phoneNumber.value));
    if (phoneNumber.error) {
      setPhoneNumber((oldObject) => ({
        ...oldObject,
        error: "",
      }));
    }
  }, [phoneNumber.value]);

  useEffect(() => {
    setDisabledCode(!code.value || phoneNumber.status !== "success");
    if (code.error) {
      setCode((oldObject) => ({
        ...oldObject,
        error: "",
      }));
    }
  }, [code.value, phoneNumber.status]);

  useEffect(() => {
    setDisabled(phoneNumber.status !== "success" || code.status !== "success");
    onStatusChange({
      phoneNumber: phoneNumber.status,
      code: code.status,
    });
  }, [phoneNumber.status, code.status, onStatusChange]);

  // Removed RTK Query side-effects in favor of direct Amplify calls above

  return (
    <div className="relative flex flex-col gap-y-7">
      {(isLoadingGetAttribute || isLoadingVerifyAttribute) && <Loading absolute />}
      <div className="flex flex-col gap-y-5">
        <form onSubmit={(event) => onSubmitHandler(event, "phoneNumber")}>
          <div className="flex flex-col">
            <InputWithButton
              inputProps={{
                id: "phoneNumber",
                name: "phoneNumber",
                value: phoneNumber.value,
                onChange: onChangeHandler,
                type: "tel",
                maxLength: 12,
                minLength: 12,
                pattern: "\\+389[0-9]{8}",
                placeholder: "+38912345678",
                readOnly: phoneNumber.status === "success",
              }}
              buttonProps={{
                type: "submit",
                variant: returnButtonVariant(phoneNumber),
                disabled: disabledPhoneNumber,
                className: "min-w-[150px] max-w-[150px] px-2",
                children: returnPhoneNumberText,
              }}
            />
            {(sent || !!phoneNumber.error) && (
              <div className={twMerge("mt-1 flex", sent && !!phoneNumber.error ? "justify-between" : "self-start")}>
                <p className="cursor-pointer self-start text-xs leading-6" onClick={resetState}>
                  {t("profile.twoFactorAuth.changeNumber")}
                </p>
                <ValidationMessage>{phoneNumber.error}</ValidationMessage>
              </div>
            )}
          </div>
        </form>
        <form onSubmit={(event) => onSubmitHandler(event, "code")}>
          <div className="flex flex-col">
            <div className="flex">
              <InputWithButton
                inputProps={{
                  id: "code",
                  name: "code",
                  value: code.value,
                  onChange: onChangeHandler,
                  placeholder: t("profile.twoFactorAuth.enterSMSCode2"),
                  maxLength: 6,
                  minLength: 6,
                  readOnly: code.status === "success" || phoneNumber.status !== "success",
                }}
                buttonProps={{
                  type: "submit",
                  variant: returnButtonVariant(code),
                  disabled: disabledCode,
                  className: "min-w-[150px] max-w-[150px] px-2",
                  children: returnCodeText,
                }}
              />
            </div>
            <div className="mt-2 flex justify-end">
              <ValidationMessage>{code.error}</ValidationMessage>
            </div>
          </div>
        </form>
      </div>
      <div className="flex flex-1">
        <div className="flex flex-1 items-center justify-center">{cancel}</div>
        <div className="flex-1">
          <Button disabled={disabled} onClick={onContinue}>
            {t("common.continue")}
          </Button>
        </div>
      </div>
    </div>
  );
};
